import React, { useEffect, useState } from "react";
import "./css/setting.css";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faRightFromBracket, faPlus } from "@fortawesome/free-solid-svg-icons";
import { deleteSession } from "./common/session";

/* 휴대폰/유선 전화번호 자동 하이픈 포맷 */
function formatPhoneKOR(input = "") {
    const v = String(input).replace(/\D/g, "");
    if (!v) return "";
    if (v.startsWith("02")) {
        if (v.length <= 2) return v;
        if (v.length <= 5) return `02-${v.slice(2)}`;
        if (v.length <= 9) return `02-${v.slice(2, 5)}-${v.slice(5)}`;
        return `02-${v.slice(2, 6)}-${v.slice(6, 10)}`;
    }
    const head3 = v.slice(0, 3);
    if (v.length <= 3) return head3;
    if (v.length <= 7) return `${head3}-${v.slice(3)}`;
    if (v.length <= 10) return `${head3}-${v.slice(3, 6)}-${v.slice(6, 10)}`;
    return `${head3}-${v.slice(3, 7)}-${v.slice(7, 11)}`;
}

export default function Profile() {
    const navigate = useNavigate();

    const API_BASE_URL =
        process.env.REACT_APP_API_BASE_URL || "http://localhost:4000/api";

    const [profile, setProfile] = useState({
        name: "",
        email: "",
        phone: "",      // 숫자만 저장
        birthday: "",   // YYYY-MM-DD
        level: null,
        position: "",
        yearAssociation: "",
        localAssociation: "",
        church: "",
        memo: "",
        picture: null,
        profileImg: null,
    });

    const [draft, setDraft] = useState(profile);
    const [isEditing, setIsEditing] = useState(false);
    const [previewImage, setPreviewImage] = useState(null); // 미리보기 이미지 URL
    const [selectedImageFile, setSelectedImageFile] = useState(null); // 선택된 이미지 파일
    const [hasPendingRequest, setHasPendingRequest] = useState(false); // pending 상태인 수정 요청이 있는지
    const [showLogoutModal, setShowLogoutModal] = useState(false); // 로그아웃 확인 모달
    const [isLoading, setIsLoading] = useState(true); // 로딩 상태

    // 프로필 정보 로드 함수 (재사용 가능하도록 분리)
    const loadProfile = async () => {
        setIsLoading(true); // 로딩 시작
        // localStorage에서 이메일 가져오기
        const email = localStorage.getItem("hb_serverEmail") ||
            localStorage.getItem("hb_googleEmail") ||
            "";

        if (!email) {
            console.log("Setting 페이지: 이메일이 없어서 사용자 정보를 가져올 수 없습니다.");
            // Fallback to localStorage
            try {
                const savedName =
                    localStorage.getItem("hb_serverName") ||
                    localStorage.getItem("hb_googleName") ||
                    "";
                const savedEmail =
                    localStorage.getItem("hb_serverEmail") ||
                    localStorage.getItem("hb_googleEmail") ||
                    "";
                setProfile((p) => ({
                    ...p,
                    name: savedName || p.name,
                    email: savedEmail || p.email,
                }));
            } catch (e) {
                console.error("Failed to load profile from localStorage", e);
            }
            setIsLoading(false); // 로딩 완료
            return;
        }

        // 이메일로 사용자 정보 조회 (토큰 검증 없음, 무조건 호출)
        try {
            console.log("Setting 페이지: 이메일로 사용자 정보 조회 시작:", email);
            const res = await fetch(`${API_BASE_URL}/auth/user-by-email?email=${encodeURIComponent(email)}`, {
                method: "GET",
            });

            if (res.ok) {
                const data = await res.json();
                const user = data.user || {};

                console.log("Setting 페이지: 서버 응답 데이터:", data);
                console.log("Setting 페이지: user 객체:", user);

                // birthday를 YYYY-MM-DD 로 정규화
                let birthdayValue = "";
                if (user.birthday) {
                    const d = new Date(user.birthday);
                    if (!Number.isNaN(d.getTime())) {
                        birthdayValue = d.toISOString().slice(0, 10);
                    } else if (/^\d{4}-\d{2}-\d{2}/.test(user.birthday)) {
                        birthdayValue = user.birthday.slice(0, 10);
                    }
                }

                const next = {
                    name: user.name || "",
                    email: user.email || "",
                    phone: user.phone || "",
                    birthday: birthdayValue,
                    level: user.level ?? null,
                    position: user.position || "",
                    yearAssociation: user.yearAssociation || user.year_association || "",
                    localAssociation: user.localAssociation || user.local_association || "",
                    church: user.church || "",
                    memo: user.memo || user.signupMessage || "",
                    picture: user.picture || null,
                    profileImg: user.profileImg || null,
                };

                setProfile(next);
                setDraft(next);

                // 서버 값 로컬에도 저장 (다른 화면에서 재사용)
                if (next.name) {
                    localStorage.setItem("hb_serverName", next.name);
                    localStorage.setItem("hb_googleName", next.name);
                }
                if (next.email) {
                    localStorage.setItem("hb_serverEmail", next.email);
                    localStorage.setItem("hb_googleEmail", next.email);
                }
                if (next.level != null) {
                    localStorage.setItem("hb_userLevel", String(next.level));
                }
                if (user.picture) {
                    localStorage.setItem("hb_googlePicture", user.picture);
                }
                if (user.birthday !== undefined) {
                    localStorage.setItem("hb_birthday", user.birthday || "");
                }
                if (user.id) {
                    localStorage.setItem("hb_backendUserId", String(user.id));
                }

                console.log("Setting 페이지: 서버에서 최신 사용자 정보를 가져왔습니다.", user);
                setIsLoading(false); // 로딩 완료
                return next;
            } else {
                console.error("Setting 페이지: 서버에서 사용자 정보를 가져오지 못했습니다:", res.status);
                // Fallback to localStorage
                try {
                    const savedName =
                        localStorage.getItem("hb_serverName") ||
                        localStorage.getItem("hb_googleName") ||
                        "";
                    const savedEmail =
                        localStorage.getItem("hb_serverEmail") ||
                        localStorage.getItem("hb_googleEmail") ||
                        "";
                    setProfile((p) => ({
                        ...p,
                        name: savedName || p.name,
                        email: savedEmail || p.email,
                    }));
                } catch (e) {
                    console.error("Failed to load profile from localStorage", e);
                }
                setIsLoading(false); // 로딩 완료
                return null;
            }
        } catch (e) {
            console.error("Setting 페이지: 사용자 정보 조회 중 오류 발생", e);
            // Fallback to localStorage
            try {
                const savedName =
                    localStorage.getItem("hb_serverName") ||
                    localStorage.getItem("hb_googleName") ||
                    "";
                const savedEmail =
                    localStorage.getItem("hb_serverEmail") ||
                    localStorage.getItem("hb_googleEmail") ||
                    "";
                setProfile((p) => ({
                    ...p,
                    name: savedName || p.name,
                    email: savedEmail || p.email,
                }));
            } catch (err) {
                console.error("Failed to load profile from localStorage", err);
            }
            setIsLoading(false); // 로딩 완료
            return null;
        }
    };

    // pending 상태 확인 함수
    const checkPendingRequest = async () => {
        const token = localStorage.getItem("hb_backendToken");
        const email = localStorage.getItem("hb_serverEmail") || localStorage.getItem("hb_googleEmail");
        
        // 토큰이 없고 이메일도 없으면 확인 불가
        if (!token && !email) {
            setHasPendingRequest(false);
            return;
        }

        try {
            const headers = {};
            if (token) {
                headers.Authorization = `Bearer ${token}`;
            }
            
            // 이메일이 있으면 query parameter로 추가
            const url = email 
                ? `${API_BASE_URL}/auth/profile-change-request-status?email=${encodeURIComponent(email)}`
                : `${API_BASE_URL}/auth/profile-change-request-status`;

            console.log("🔍 [checkPendingRequest] API 호출:", url);
            const res = await fetch(url, {
                method: "GET",
                headers: headers,
            });
            
            console.log("🔍 [checkPendingRequest] 응답 상태:", res.status);

            // 401 에러가 발생해도 이메일이 있으면 계속 진행 (백엔드에서 이메일로 처리)
            if (res.status === 401 && !email) {
                console.log("토큰이 만료되었고 이메일 정보가 없어 pending 상태 확인 불가");
                setHasPendingRequest(false);
                return;
            }

            if (res.ok) {
                const data = await res.json();
                console.log("🔍 [checkPendingRequest] 응답 데이터:", data);
                setHasPendingRequest(data.hasPendingRequest || false);
                console.log("🔍 [checkPendingRequest] hasPendingRequest 설정:", data.hasPendingRequest || false);
            } else {
                const errorData = await res.json().catch(() => ({}));
                console.error("🔍 [checkPendingRequest] 에러 응답:", res.status, errorData);
                setHasPendingRequest(false);
            }
        } catch (e) {
            console.error("Failed to check pending request:", e);
            setHasPendingRequest(false);
        }
    };

    // 프로필 정보 로드 (이메일로 무조건 호출)
    useEffect(() => {
        loadProfile();
        checkPendingRequest();
    }, [API_BASE_URL]);
    // 로그아웃 버튼 클릭 시 모달 표시
    const handleLogoutClick = () => {
        setShowLogoutModal(true);
    };

    // 로그아웃 확인 후 실제 로그아웃 실행
    const handleLogoutConfirm = async () => {
        setShowLogoutModal(false);
        
        // 1) 세션 삭제 (MySQL에서 삭제 및 모든 스토리지 삭제)
        // deleteSession() 내부에서 clearAllStorage()와 RN 메시지 전송이 모두 처리됨
        await deleteSession();

        // 2) 안드로이드 네이티브 WebView 인 경우 → 네이티브로 신호 전송 후 반환
        // (deleteSession() 내부에서 이미 전송되지만, 확실하게 한 번 더 전송)
        if (window.ReactNativeWebView) {
            try {
                window.ReactNativeWebView.postMessage(
                    JSON.stringify({ 
                        type: "LOGOUT",
                        clearAll: true 
                    })
                );
                console.log('📱 RN에 로그아웃 메시지 전송 완료');
            } catch (e) {
                console.error("RN postMessage 오류:", e);
            }
            return;
        }

        // 3) 일반 웹 브라우저인 경우 → 로그인 화면으로 이동
        navigate("/login", { replace: true });
    };

    // 로그아웃 모달 취소
    const handleLogoutCancel = () => {
        setShowLogoutModal(false);
    };

    const handleEditToggle = async () => {
        if (!isEditing) {
            // 수정 모드 시작 - 최신 정보 다시 불러오기
            console.log("Setting 페이지: 수정 모드 시작 - 최신 정보 불러오기");
            const latestProfile = await loadProfile();
            if (latestProfile) {
                setDraft(latestProfile);
            } else {
                // 최신 정보를 불러오지 못한 경우 현재 profile 사용
                setDraft(profile);
            }
            // 미리보기 초기화
            setPreviewImage(null);
            setSelectedImageFile(null);
        } else {
            // 취소 - 원래 상태로 복원
            setDraft(profile);
            // 미리보기 초기화 (원래 이미지로 복원)
            setPreviewImage(null);
            setSelectedImageFile(null);
        }
        setIsEditing((v) => !v);
    };

    const handleChange = (field, value) => {
        setDraft((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // 파일 크기 제한 (5MB)
        if (file.size > 5 * 1024 * 1024) {
            alert("이미지 크기는 5MB 이하여야 합니다.");
            return;
        }

        // 파일 타입 확인
        if (!file.type.startsWith("image/")) {
            alert("이미지 파일만 업로드 가능합니다.");
            return;
        }

        // 파일을 선택하여 미리보기 생성
        setSelectedImageFile(file);
        const reader = new FileReader();
        reader.onloadend = () => {
            setPreviewImage(reader.result);
        };
        reader.readAsDataURL(file);
    };

    const handleSave = async () => {
        const token = localStorage.getItem("hb_backendToken");
        const email = localStorage.getItem("hb_serverEmail") || localStorage.getItem("hb_googleEmail");
        
        // 토큰이 없거나 이메일이 없으면 경고만 표시하고 계속 진행
        if (!token && !email) {
            alert("이메일 정보가 없어 프로필을 수정할 수 없습니다.");
            return;
        }

        try {
            // 이미지가 선택된 경우 먼저 업로드
            if (selectedImageFile) {
                const formData = new FormData();
                formData.append("profileImg", selectedImageFile);
                if (email) {
                    formData.append("email", email);
                }

                const headers = {};
                if (token) {
                    headers.Authorization = `Bearer ${token}`;
                }

                const imageRes = await fetch(`${API_BASE_URL}/auth/profile-image`, {
                    method: "POST",
                    headers: headers,
                    body: formData,
                });

                // 401 에러가 발생해도 이메일이 있으면 계속 진행 (백엔드에서 이메일로 처리)
                if (imageRes.status === 401 && !email) {
                    alert("로그인이 만료되었습니다. 이메일 정보가 없어 수정할 수 없습니다.");
                    return;
                }

                const imageData = await imageRes.json();
                if (!imageRes.ok) {
                    console.error("Failed to upload profile image:", imageData);
                    alert(`이미지 업로드에 실패했습니다: ${imageData.message || "알 수 없는 오류"}`);
                    return;
                }

                // 이미지 업로드 성공 - draft에 profileImg 업데이트는 하지 않음
                // (승인 전까지는 원본 데이터를 보여줘야 하므로)
                console.log("✅ 프로필 이미지 수정 요청 저장 완료");
            }

            // 프로필 정보 업데이트
            const headers = {
                "Content-Type": "application/json",
            };
            if (token) {
                headers.Authorization = `Bearer ${token}`;
            }

            const requestBody = {
                name: draft.name || null,
                phone: draft.phone ? draft.phone.replace(/\D/g, "") : null,
                birthday: draft.birthday || null,
                position: draft.position || null,
                yearAssociation: draft.yearAssociation || null,
                localAssociation: draft.localAssociation || null,
                church: draft.church || null,
                memo: draft.memo || null,
            };
            
            // 이메일이 있으면 요청 본문에 추가
            if (email) {
                requestBody.email = email;
            }

            const res = await fetch(`${API_BASE_URL}/auth/profile`, {
                method: "PUT",
                headers: headers,
                body: JSON.stringify(requestBody),
            });

            // 401 에러가 발생해도 이메일이 있으면 계속 진행 (백엔드에서 이메일로 처리)
            if (res.status === 401 && !email) {
                alert("로그인이 만료되었습니다. 이메일 정보가 없어 수정할 수 없습니다.");
                return;
            }

            const data = await res.json();
            if (!res.ok) {
                console.error("Failed to update profile on server:", data);
                alert(`프로필 수정 요청 저장에 실패했습니다: ${data.message || "알 수 없는 오류"}`);
            } else {
                // 수정 요청이 저장되었으므로, 승인 전까지는 원본 users 데이터를 보여줘야 함
                // 서버에서 원본 데이터를 다시 불러오기
                console.log("✅ 프로필 수정 요청 저장 완료 - 원본 데이터 다시 불러오기");
                await loadProfile();
                
                // pending 상태 다시 확인
                await checkPendingRequest();
                
                setIsEditing(false);
                // 미리보기 초기화
                setPreviewImage(null);
                setSelectedImageFile(null);
                
                // 성공 메시지 표시
                alert("프로필 수정 요청이 저장되었습니다. 관리자 승인을 기다려주세요.");
            }
        } catch (e) {
            console.error("Failed to update profile on server", e);
            alert("프로필 수정 중 오류가 발생했습니다. 다시 시도해주세요.");
        }
    };

    const displayPhone = draft.phone || profile.phone
        ? formatPhoneKOR((isEditing ? draft.phone : profile.phone) || "")
        : "";

    // 프로필 이미지 URL (미리보기 우선, 없으면 기존 이미지)
    const profileImageUrl = previewImage 
        ? previewImage 
        : (profile.profileImg 
            ? (profile.profileImg.startsWith('http') 
                ? profile.profileImg 
                : `https://dev.asv.mycert.or.kr${profile.profileImg.startsWith('/') ? profile.profileImg : '/' + profile.profileImg}`)
            : profile.picture || null);

    return (
        <div className="container-wrap setting-wrap">
            <div className="setting-viewport">
                {/* 헤더 로고 + 로그아웃 아이콘 */}
                <header className="setting-header">
                    <img
                        src="/img/myCert.png"
                        alt="myCert"
                        className="setting-logo"
                    />
                    <button
                        className="setting-logout-icon"
                        type="button"
                        onClick={handleLogoutClick}
                    >
                        <FontAwesomeIcon icon={faRightFromBracket} />
                    </button>
                </header>

                {/* 본문 (스크롤 영역) */}
                <main className="setting-main profile-main">
                    <div className="profile-scroll">
                        {/* 프로필 이미지 */}
                        <div className="profile-avatar-wrap" style={{ position: "relative" }}>
                            {isLoading ? (
                                <div className="profile-avatar-skeleton"></div>
                            ) : profileImageUrl ? (
                                <img
                                    src={profileImageUrl}
                                    alt="프로필"
                                    className="profile-avatar-img"
                                />
                            ) : (
                                <div className="profile-avatar-placeholder">
                                    <FontAwesomeIcon icon={faUser} style={{fontSize:"50px"}} />
                                </div>
                            )}
                            {isEditing && (
                                <label
                                    htmlFor="profile-image-upload"
                                    className="profile-avatar-upload-btn"
                                >
                                    <FontAwesomeIcon icon={faPlus} style={{ fontSize: "18px" }} />
                                    <input
                                        id="profile-image-upload"
                                        type="file"
                                        accept="image/*"
                                        style={{ display: "none" }}
                                        onChange={handleImageChange}
                                    />
                                </label>
                            )}
                        </div>

                        {/* 이름 & 이메일 요약 */}
                        <div className="profile-summary">
                            {isLoading ? (
                                <div className="profile-name-skeleton"></div>
                            ) : (
                                <div className="profile-name-main">
                                    {profile.name || "-"}
                                </div>
                            )}
                            {/* <div className="profile-email-main">
                                {profile.email || "-"}
                            </div> */}
                        </div>
                    </div>

                    {/* 스크롤 가능한 필드 영역 */}
                    <div className="profile-scroll-fields">
                        {/* 상세 필드 */}
                        <section className="profile-section">
                            {/* 이름 (수정 가능) */}

                            {/* 이메일 (수정 불가) */}
                            <div className="profile-row">
                                <span className="profile-label">이메일</span>
                                <span className="profile-value">
                                    {profile.email || "-"}
                                </span>
                            </div>

                            {/* 등급 (수정 불가) */}
                            <div className="profile-row">
                                <span className="profile-label">등급</span>
                                <span className="profile-value">
                                    {profile.level === 1
                                        ? "정회원"
                                        : profile.level === 2
                                            ? "준회원"
                                            : "-"}
                                </span>
                            </div>
                            <div className={`profile-row ${isEditing ? 'editing' : ''}`}>
                                <span className="profile-label">이름</span>
                                {isEditing ? (
                                    <input
                                        className="profile-input"
                                        type="text"
                                        value={draft.name}
                                        onChange={(e) =>
                                            handleChange("name", e.target.value)
                                        }
                                        placeholder="이름"
                                    />
                                ) : (
                                    <span className="profile-value">
                                        {profile.name || "-"}
                                    </span>
                                )}
                            </div>

                            {/* 휴대폰 */}
                            <div className={`profile-row ${isEditing ? 'editing' : ''}`}>
                                <span className="profile-label">휴대폰</span>
                                {isEditing ? (
                                    <input
                                        className="profile-input"
                                        type="tel"
                                        value={draft.phone}
                                        onChange={(e) =>
                                            handleChange(
                                                "phone",
                                                formatPhoneKOR(e.target.value)
                                            )
                                        }
                                        placeholder="010-0000-0000"
                                    />
                                ) : (
                                    <span className="profile-value">
                                        {displayPhone || "-"}
                                    </span>
                                )}
                            </div>

                            {/* 생년월일 */}
                            {/* <div className={`profile-row ${isEditing ? 'editing' : ''}`}>
                                <span className="profile-label">생년월일</span>
                                {isEditing ? (
                                    <input
                                        className="profile-input"
                                        type="date"
                                        value={draft.birthday || ""}
                                        style={{
                                            padding:"5px 10px"
                                        }}
                                        onChange={(e) =>
                                            handleChange("birthday", e.target.value)
                                        }
                                    />
                                ) : (
                                    <span className="profile-value">
                                        {profile.birthday || "-"}
                                    </span>
                                )}
                            </div> */}

                            {/* 직책 */}
                            <div className={`profile-row ${isEditing ? 'editing' : ''}`}>
                                <span className="profile-label">호칭</span>
                                {isEditing ? (
                                    <input
                                        className="profile-input"
                                        value={draft.position}
                                        onChange={(e) =>
                                            handleChange("position", e.target.value)
                                        }
                                        placeholder="호칭을 입력하세요"
                                    />
                                ) : (
                                    <span className="profile-value">
                                        {profile.position || "-"}
                                    </span>
                                )}
                            </div>

                            {/* 소속 연회 */}
                            <div className={`profile-row ${isEditing ? 'editing' : ''}`}>
                                <span className="profile-label">소속 연회</span>
                                {isEditing ? (
                                    <input
                                        className="profile-input"
                                        value={draft.yearAssociation}
                                        onChange={(e) =>
                                            handleChange("yearAssociation", e.target.value)
                                        }
                                        placeholder="소속 연회"
                                    />
                                ) : (
                                    <span className="profile-value">
                                        {profile.yearAssociation || "-"}
                                    </span>
                                )}
                            </div>

                            {/* 소속 지방회 */}
                            <div className={`profile-row ${isEditing ? 'editing' : ''}`}>
                                <span className="profile-label">소속 지방회</span>
                                {isEditing ? (
                                    <input
                                        className="profile-input"
                                        value={draft.localAssociation}
                                        onChange={(e) =>
                                            handleChange("localAssociation", e.target.value)
                                        }
                                        placeholder="소속 지방회"
                                    />
                                ) : (
                                    <span className="profile-value">
                                        {profile.localAssociation || "-"}
                                    </span>
                                )}
                            </div>

                            {/* 소속 교회 */}
                            <div className={`profile-row ${isEditing ? 'editing' : ''}`}>
                                <span className="profile-label">소속 교회</span>
                                {isEditing ? (
                                    <input
                                        className="profile-input"
                                        value={draft.church}
                                        onChange={(e) =>
                                            handleChange("church", e.target.value)
                                        }
                                        placeholder="소속 교회"
                                    />
                                ) : (
                                    <span className="profile-value">
                                        {profile.church || "-"}
                                    </span>
                                )}
                            </div>

                            {/* 메모 */}
                            <div className={`profile-row profile-row-memo ${isEditing ? 'editing' : ''}`}>
                                <span className="profile-label">메모</span>
                                {isEditing ? (
                                    <textarea
                                        className="profile-textarea"
                                        rows={3}
                                        maxLength={200}
                                        value={draft.memo}
                                        onChange={(e) =>
                                            handleChange("memo", e.target.value)
                                        }
                                        placeholder="메모를 입력하세요"
                                    />
                                ) : (
                                    <span className="profile-value profile-value-memo">
                                        {profile.memo || "-"}
                                    </span>
                                )}
                            </div>
                        </section>
                    </div>

                    {/* 하단 수정/저장 버튼 (고정) */}
                    <div className="profile-actions">
                        {!isEditing ? (
                            <button
                                type="button"
                                className={`profile-edit-btn ${hasPendingRequest ? 'disabled' : ''}`}
                                onClick={handleEditToggle}
                                disabled={hasPendingRequest}
                            >
                                {hasPendingRequest ? '수정 승인 대기중' : '수정하기'}
                            </button>
                        ) : (
                            <>
                                <button
                                    type="button"
                                    className="profile-cancel-btn"
                                    onClick={handleEditToggle}
                                >
                                    취소
                                </button>
                                <button
                                    type="button"
                                    className="profile-save-btn"
                                    onClick={handleSave}
                                >
                                    승인요청
                                </button>
                            </>
                        )}
                    </div>
                </main>
            </div>

            {/* 로그아웃 확인 모달 */}
            {showLogoutModal && (
                <div 
                    className="setting-modal-overlay" 
                    onClick={handleLogoutCancel}
                >
                    <div 
                        className="setting-modal" 
                        onClick={(e) => e.stopPropagation()}
                        style={{ maxWidth: "400px", width: "90%" }}
                    >
                        <div className="setting-modal-bar">
                            <b>로그아웃</b>
                        </div>
                        <div className="setting-modal-body" style={{ padding: "24px" }}>
                            <div style={{
                                fontSize: "18px",
                                fontWeight: "600",
                                color: "#111",
                                marginBottom: "12px",
                                textAlign: "center"
                            }}>
                                로그아웃 하시겠습니까?
                            </div>
                            <div style={{
                                fontSize: "14px",
                                color: "#666",
                                textAlign: "center",
                                lineHeight: "1.5"
                            }}>
                                로그아웃 시 모든 세션 정보가 삭제됩니다.
                            </div>
                        </div>
                        <div className="setting-modal-actions" style={{ 
                            display: "flex", 
                            gap: "10px", 
                            padding: "16px 20px",
                            borderTop: "1px solid #eee"
                        }}>
                            <button
                                className="profile-cancel-btn"
                                onClick={handleLogoutCancel}
                                style={{ flex: 1 }}
                            >
                                취소
                            </button>
                            <button
                                className="profile-save-btn"
                                onClick={handleLogoutConfirm}
                                style={{ flex: 1, background: "#ff4d4f" }}
                            >
                                로그아웃
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
